import React, { useEffect, useMemo, useState } from 'react'
import { AxiosResponse } from 'axios'
import { useMutation } from '@tanstack/react-query'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import {
  Alert,
  AlertIcon,
  Button,
  Center,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack
} from '@chakra-ui/react'
import { subMonths } from 'date-fns'
import { formatISODate } from '@homevest/utils/dates'
import { UTILITY_BILL_CHARGE_TYPES } from '@homevest/utils/utilities'
import type {
  CreateUtilityChargebackPayload,
  CreateUtilityChargebackResponse
} from '@homevest/types/utilities'
import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import { useUtilityBillsForPropertyQuery } from 'graphql/generated'
import axios from 'lib/axios'
import { useBuildiumPostingGuard } from 'lib/buildiumBooksLock'
import { formatMoney } from 'lib/numbers'
import BillSelectionStep from './BillSelectionStep'
import ChargebackDetailsStep from './ChargebackDetailsStep'
import {
  LIKELY_CANDIDATE_LOOKBACK_MONTHS,
  formatDisplayDate,
  getBillPaymentStatusBadgeColor,
  getBillPaymentStatusLabel,
  getDefaultAmountForBill,
  getDefaultNote,
  getErrorMessage,
  getInitialWarningForSelectedBill,
  getStatusBadgeColor,
  getStatusDisplay,
  getUtilityTypeLabel,
  parseBillDate
} from './helpers'
import { UtilityBill, UtilityChargebackModalProps } from './types'

export default function UtilityChargebackModal({
  finalLiabilityDate,
  isOpen,
  occupancyDate,
  onBack,
  onClose,
  onSuccess,
  portfolioHomeId,
  rentalId
}: UtilityChargebackModalProps) {
  const [selectedBillId, setSelectedBillId] = useState<string>()
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(formatISODate(new Date()))
  const [note, setNote] = useState('')
  const [showAllBills, setShowAllBills] = useState(false)
  const [warningMessage, setWarningMessage] = useState<string>()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [requiresConfirmation, setRequiresConfirmation] = useState(false)
  const utilityBillsQueryOptions = useMemo(
    () => ({
      variables: { portfolioHomeId },
      pause: !isOpen || !portfolioHomeId,
      requestPolicy: 'network-only' as const,
      context: {
        suspense: false
      }
    }),
    [isOpen, portfolioHomeId]
  )

  const [{ data, fetching, error }] = useUtilityBillsForPropertyQuery(utilityBillsQueryOptions)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setSelectedBillId(undefined)
    setAmount('')
    setDate(formatISODate(new Date()))
    setNote('')
    setShowAllBills(false)
    setWarningMessage(undefined)
    setErrorMessage(undefined)
    setRequiresConfirmation(false)
  }, [isOpen, rentalId])

  const selectedBill = data?.utility_bills.find((bill) => bill.id === selectedBillId)

  useEffect(() => {
    if (!selectedBill) {
      return
    }

    const initialWarning = getInitialWarningForSelectedBill(selectedBill, rentalId)
    const defaultAmount = getDefaultAmountForBill({
      bill: selectedBill,
      finalLiabilityDate,
      occupancyDate,
      rentalId
    })

    setAmount(defaultAmount.toFixed(2))
    setDate(formatISODate(new Date()))
    setNote(getDefaultNote(selectedBill))
    setWarningMessage(initialWarning)
    setErrorMessage(undefined)
    setRequiresConfirmation(Boolean(initialWarning))
  }, [finalLiabilityDate, occupancyDate, rentalId, selectedBill])

  const bills = useMemo(() => {
    const unsortedBills = data?.utility_bills ?? []

    return [...unsortedBills].sort((left, right) => {
      const leftStatusPriority =
        getStatusDisplay(left, rentalId).description === 'No chargeback' ? 0 : 1
      const rightStatusPriority =
        getStatusDisplay(right, rentalId).description === 'No chargeback' ? 0 : 1

      if (leftStatusPriority !== rightStatusPriority) {
        return leftStatusPriority - rightStatusPriority
      }

      const leftBillDate = parseBillDate(left.bill_date)?.getTime() ?? 0
      const rightBillDate = parseBillDate(right.bill_date)?.getTime() ?? 0

      return rightBillDate - leftBillDate
    })
  }, [data?.utility_bills, rentalId])

  const likelyCandidateBills = useMemo(() => {
    const recentBillCutoff = subMonths(new Date(), LIKELY_CANDIDATE_LOOKBACK_MONTHS)

    return bills.filter((bill) => {
      const billChargeback = bill.utility_chargebacks.find(
        (chargeback) => chargeback.rental_id === rentalId
      )
      const billDate = parseBillDate(bill.bill_date)

      return (
        !billChargeback &&
        bill.charge_type === UTILITY_BILL_CHARGE_TYPES.SERVICE_CHARGE &&
        !!billDate &&
        billDate >= recentBillCutoff
      )
    })
  }, [bills, rentalId])

  const displayedBills =
    showAllBills || likelyCandidateBills.length === 0 ? bills : likelyCandidateBills
  const selectedBillStatus = selectedBill ? getStatusDisplay(selectedBill, rentalId) : undefined
  const isStepTwo = !!selectedBill
  const {
    booksLock,
    hasValidPostingDate,
    isBooksLockLoading,
    isBooksLockError,
    isBuildiumBooksLocked
  } = useBuildiumPostingGuard({
    date,
    enabled: isOpen && isStepTwo,
    rentalId
  })

  const utilityBillColumns = useMemo<ColDef<UtilityBill>[]>(
    () => [
      {
        headerName: '',
        minWidth: 120,
        maxWidth: 120,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: ({ data: bill }: ICellRendererParams<UtilityBill>) => {
          if (!bill) {
            return null
          }

          const status = getStatusDisplay(bill, rentalId)

          return (
            <Button
              size='sm'
              onClick={() => setSelectedBillId(bill.id)}
              isDisabled={!status.isSelectable}
            >
              Select
            </Button>
          )
        }
      },
      {
        headerName: 'Status',
        minWidth: 200,
        valueGetter: ({ data: bill }) => (bill ? getStatusDisplay(bill, rentalId).description : ''),
        cellRenderer: ({ data: bill }: ICellRendererParams<UtilityBill>) => {
          if (!bill) {
            return null
          }

          const status = getStatusDisplay(bill, rentalId)

          return mapContentToBadge(status.description, {
            dot: false,
            size: 'xs',
            colorOverride: getStatusBadgeColor(status.colorScheme),
            formatFn: (value) => value
          })
        }
      },
      {
        headerName: 'Bill Status',
        minWidth: 140,
        valueGetter: ({ data: bill }) => getBillPaymentStatusLabel(bill?.status),
        cellRenderer: ({ data: bill }: ICellRendererParams<UtilityBill>) => {
          if (!bill) {
            return null
          }

          return mapContentToBadge(getBillPaymentStatusLabel(bill.status), {
            dot: false,
            size: 'xs',
            colorOverride: getBillPaymentStatusBadgeColor(bill.status),
            formatFn: (value) => value
          })
        }
      },
      {
        headerName: 'Utility',
        valueGetter: ({ data: bill }) => getUtilityTypeLabel(bill?.utility_type.name)
      },
      {
        headerName: 'Service Period',
        valueGetter: ({ data: bill }) =>
          bill
            ? `${formatDisplayDate(bill.service_start_date)} to ${formatDisplayDate(
                bill.service_end_date
              )}`
            : ''
      },
      {
        headerName: 'Bill Date',
        valueGetter: ({ data: bill }) => formatDisplayDate(bill?.bill_date)
      },
      {
        headerName: 'Amount',
        filter: 'agNumberColumnFilter',
        valueGetter: ({ data: bill }) => (bill ? Number(bill.amount) : null),
        valueFormatter: ({ value }) => (value == null ? '' : formatMoney(Number(value), 2, '$'))
      },
      {
        headerName: 'Vendor',
        field: 'vendor',
        wrapText: true,
        autoHeight: true,
        minWidth: 220,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.25'
        }
      },
      {
        headerName: 'Charge Type',
        valueGetter: ({ data: bill }) => getUtilityTypeLabel(bill?.charge_type)
      }
    ],
    [rentalId]
  )

  const numericAmount = Number(amount)
  const isFormValid =
    !!selectedBill &&
    hasValidPostingDate &&
    Number.isFinite(numericAmount) &&
    numericAmount > 0 &&
    !isBooksLockLoading &&
    !isBooksLockError &&
    !isBuildiumBooksLocked

  const { mutateAsync: submitChargeback, isLoading } = useMutation<
    AxiosResponse<CreateUtilityChargebackResponse>,
    unknown,
    { confirmed?: boolean }
  >({
    mutationFn: async ({ confirmed }) =>
      axios.post('/admin/utility_chargebacks/create', {
        utility_bill_id: selectedBill?.id,
        rental_id: rentalId,
        amount: numericAmount,
        date,
        note: note || undefined,
        confirmed
      } as CreateUtilityChargebackPayload)
  })

  const handleSubmit = async () => {
    setErrorMessage(undefined)

    try {
      const result = await submitChargeback({ confirmed: requiresConfirmation || undefined })

      if ('requires_confirmation' in result.data) {
        setWarningMessage(result.data.warning)
        setRequiresConfirmation(true)
        return
      }

      await onSuccess([result.data.utility_chargeback.rental_liability_id])
      onClose()
    } catch (mutationError) {
      setErrorMessage(getErrorMessage(mutationError))
    }
  }

  return (
    <Modal size='6xl' isOpen={isOpen} onClose={onClose} blockScrollOnMount={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isStepTwo ? 'Create Utility Chargeback' : 'Select a Utility Bill to Charge Back'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            {!isStepTwo && (
              <>
                {fetching && (
                  <Center py={12}>
                    <Spinner />
                  </Center>
                )}

                {error && (
                  <Alert status='error'>
                    <AlertIcon />
                    Unable to load utility bills for this property.
                  </Alert>
                )}

                {!fetching && !error && (
                  <BillSelectionStep
                    bills={displayedBills}
                    columns={utilityBillColumns}
                    likelyCandidateBills={likelyCandidateBills}
                    onShowAllBillsChange={setShowAllBills}
                    rentalId={rentalId}
                    showAllBills={showAllBills}
                    totalBillCount={bills.length}
                  />
                )}
              </>
            )}

            {isStepTwo && selectedBill && (
              <ChargebackDetailsStep
                amount={amount}
                booksLockDate={booksLock?.lockDate}
                date={date}
                errorMessage={errorMessage}
                isBooksLockError={isBooksLockError}
                isBooksLockLoading={isBooksLockLoading}
                isBuildiumBooksLocked={isBuildiumBooksLocked}
                note={note}
                numericAmount={numericAmount}
                onAmountChange={setAmount}
                onDateChange={setDate}
                onNoteChange={setNote}
                selectedBill={selectedBill}
                selectedBillStatus={selectedBillStatus}
                warningMessage={warningMessage}
              />
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Flex w='100%' justify='space-between' align='center'>
            <Button
              variant='outline'
              onClick={!isStepTwo && onBack ? onBack : onClose}
              isDisabled={isLoading}
            >
              {!isStepTwo && onBack ? 'Back' : 'Cancel'}
            </Button>
            <HStack>
              {isStepTwo && (
                <Button
                  variant='outline'
                  onClick={() => {
                    setSelectedBillId(undefined)
                    setWarningMessage(undefined)
                    setErrorMessage(undefined)
                    setRequiresConfirmation(false)
                  }}
                  isDisabled={isLoading}
                >
                  Back
                </Button>
              )}
              {isStepTwo && (
                <Button
                  colorScheme={requiresConfirmation ? 'orange' : 'teal'}
                  onClick={handleSubmit}
                  isDisabled={!isFormValid || isLoading}
                  isLoading={isLoading}
                >
                  {requiresConfirmation ? 'Confirm Create' : 'Create Chargeback'}
                </Button>
              )}
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
